/**
 * How often each rule has triggered toward its limit, one member for each rule. The editor
 * reads it live while it shows a saved rule and its draft has a limit. The media_servers keep
 * the count, and the cloud sends no frame when it grows. A change of the rule can change the
 * count: a server that disables the rule at its limit, an AI tool that resets it. Thus, a change
 * of the rule reads the count of that rule again.
 */

import { type Atom, atom, type WritableAtom } from 'jotai';

import { queryAtoms } from '../queryAtoms';
import { api } from '@/api';
import type { TriggerCountSnapshot } from '@/types/agents';
import { ApplyOutcome, type ApplyResult } from '@/types/snapshot';

const counts = queryAtoms<string, TriggerCountSnapshot, TriggerCountSnapshot, string>({
    getRequestKey: (ruleId): string => ruleId,
    getPending: (ruleId): TriggerCountSnapshot => (
        { ruleId: ruleId ?? '', count: null, isLoading: true, error: null }
    ),
    load: async (ruleId): Promise<TriggerCountSnapshot> => ({
        ruleId,
        count: await api.getRuleTriggerCount(ruleId),
        isLoading: false,
        error: null,
    }),
    merge: (_previousSnapshot, page): TriggerCountSnapshot => page,
    // `apply` does not get the request, so the snapshot names its rule.
    apply: (previousSnapshot, changedRuleId): ApplyResult<TriggerCountSnapshot> => (
        changedRuleId === previousSnapshot.ruleId
            ? { outcome: ApplyOutcome.Reread }
            : { outcome: ApplyOutcome.Patched, snapshot: previousSnapshot }
    ),
});

/** The count of the rule; while mounted, it polls every `intervalMs` or faster. */
export function ruleTriggerCountAtom(
    ruleId: string, intervalMs: number,
): Atom<TriggerCountSnapshot> {
    return counts.getLive(ruleId, intervalMs);
}

/** What an editor reads without a saved rule, or without a limit in its draft: nothing loads. */
export const noTriggerCountAtom: Atom<TriggerCountSnapshot> = counts.getMember(null);

/** Set it with the id of a rule that changed elsewhere. */
export const ruleTriggerCountChangedAtom: WritableAtom<null, [string], void> = atom(
    null,
    (_get, set, ruleId: string): void => {
        set(counts.applyAll, ruleId);
    },
);
